import { tryOrElse } from 'crm-index-visualization-core/core/error/errorUtils';
import { parse } from './parser';
import * as t from 'ts-schema';
import { VizConfig } from './viz-config';
/**
 * ref: https://hubspot.slack.com/archives/C016VNJ0B8E/p1711980963074899
 *
 * There exists a "magic" property "ilsListMemberships.listId" that we are retiring in favor of 'hs_crm_search.ilsListIds'
 * This code will migrate at read time / parse time any instance of the legacy property name with the new property name
 *
 * This code can/should be used as temporary bandaid. This will be removed once the backend has migrated all views to not use the ilsListMemberships.listId property
 *
 * @throws This code may throw if the filter shape change (not type-checked by this library)
 */
export const migrateFilters = filterGroups => {
  const FE_FILTER_MIGRATION = {
    'ilsListMemberships.listId': 'hs_crm_search.ilsListIds'
  };
  const getFilterProperty = filter => {
    const propertyName = filter.property;
    const maybeOverride = FE_FILTER_MIGRATION[propertyName];
    return maybeOverride || propertyName;
  };
  return filterGroups.map(group => {
    const newFilters = group.filters.map(filter => {
      const property = getFilterProperty(filter);
      return Object.assign({}, filter, {
        property
      });
    });
    return {
      filters: newFilters
    };
  });
};

/**
 * Type definition for legacy table configuration.
 * This would typically be provided by the viz owner, but for legacy support we define it here.
 */

/**
 * Schemas for parsing legacy table configuration from BE view
 */
const legacyTableSchemas = {
  column: t.object({
    name: t.string
  }),
  state: t.object({
    numberOfFrozenColumns: t.optional(t.number)
  })
};
/**
 * Extracts legacy table configuration from a BE view
 * @throws if parsing fails and no fallback is available
 */
export function extractLegacyTableConfig(view) {
  var _state$numberOfFrozen;
  // Parse columns
  const columns = tryOrElse(() => parse(view.columns, t.array(legacyTableSchemas.column)), e => {
    console.error('Failed to parse columns from BE view:', e);
    return [];
  });

  // Parse state
  const state = tryOrElse(() => parse(view.state, legacyTableSchemas.state), e => {
    console.error('Failed to parse state from BE view:', e);
    return null;
  });

  // Convert to table config format
  return {
    columns: columns.map(col => col.name),
    numberOfFrozenColumns: (_state$numberOfFrozen = state === null || state === void 0 ? void 0 : state.numberOfFrozenColumns) !== null && _state$numberOfFrozen !== void 0 ? _state$numberOfFrozen : 0
  };
}

/**
 * Creates a VizConfig for a legacy table
 */
export function createLegacyTableConfig(view) {
  const config = extractLegacyTableConfig(view);
  return new VizConfig('table', '0', config);
}

/**
 * Type definition for legacy data well configuration.
 */

/**
 * Extracts legacy data well configuration from a BE view.
 * Uses defensive programming to find a report component in the data well area.
 */
export function extractLegacyDataWellConfig(view) {
  const reportId = tryOrElse(() => {
    var _state$viewConfigurat;
    const state = JSON.parse(view.state);
    return (_state$viewConfigurat = state.viewConfiguration[0].config.components.find(c => c.type === 'REPORT' && c.area === 'DATA_WELL')) === null || _state$viewConfigurat === void 0 ? void 0 : _state$viewConfigurat.componentId;
  }, e => {
    console.error('Failed to extract data well config:', e);
    return null;
  });
  return {
    reportId
  };
}

/**
 * Creates a VizConfig for a legacy data well
 */
export function createLegacyDataWellConfig(view) {
  const config = extractLegacyDataWellConfig(view);
  return new VizConfig('datawell', '0', config);
}

/**
 * Helper for host app to converts a segment and VizConfig back to a BackendView format. This is for legacy support only.
 *
 * @param segment The segment to convert
 * @param vizConfig The visualization configurations
 * @throws Error if the visualization type is not supported for legacy conversion
 * @returns A BackendView object
 */
export function convertToBackendView(segment, vizConfigs, baseView) {
  // Create a base BackendView object with segment data
  let fromSegment = baseView;
  if (segment) {
    var _segment$sorts$;
    fromSegment = Object.assign({}, baseView, {
      filterGroups: JSON.stringify(segment.advancedFilterGroups),
      quickFilters: JSON.stringify(segment.quickFilters),
      state: JSON.stringify({
        pipelineId: segment.pipelineId === null ? undefined : segment.pipelineId,
        // Convert FE sort format to BE sort format
        sortKey: segment.sorts.length > 1 ? segment.sorts.map(sort => sort.property) : (_segment$sorts$ = segment.sorts[0]) === null || _segment$sorts$ === void 0 ? void 0 : _segment$sorts$.property,
        order: segment.sorts.length > 0 ? segment.sorts[0].order === 'ASC' ? -1 : 1 : undefined
      })
    });
  }

  // TODO: handle multiple viz configs
  if (vizConfigs.length > 0) {
    const vizConfig = vizConfigs[0];

    // Check if this is a legacy viz type
    if (vizConfig.version !== '0') {
      throw new Error('Only legacy visualization types (version 0) are supported for conversion to BackendView. We will support other versions in the future');
    }

    // Handle specific viz types
    switch (vizConfig.vizType) {
      case 'table':
        return convertLegacyTableToBackendView(fromSegment, vizConfig.config);
      case 'datawell':
        return convertLegacyDataWellToBackendView(fromSegment, vizConfig.config);
      default:
        throw new Error(`Unsupported visualization type: ${vizConfig.vizType}`);
    }
  }
  return fromSegment;
}

/**
 * Converts a legacy table configuration to BackendView format
 * @param baseView The base BackendView object with segment data
 * @param config The legacy table configuration
 * @returns A BackendView object
 */
export function convertLegacyTableToBackendView(baseView, config) {
  // Convert columns to BE format
  const columns = config.columns.map(name => ({
    name
  }));

  // Convert state
  const state = Object.assign({}, JSON.parse(baseView.state || '{}'), {
    numberOfFrozenColumns: config.numberOfFrozenColumns
  });
  return Object.assign({}, baseView, {
    columns: JSON.stringify(columns),
    state: JSON.stringify(state)
  });
}

/**
 * Converts a legacy data well configuration to BackendView format
 * @param baseView The base BackendView object with segment data
 * @param config The legacy data well configuration
 * @returns A BackendView object
 */
function convertLegacyDataWellToBackendView(baseView, config) {
  // For data well, we need to construct the viewConfiguration with the report component
  const state = Object.assign({}, JSON.parse(baseView.state || '{}'), {
    viewConfiguration: [{
      config: {
        components: [{
          type: 'REPORT',
          area: 'DATA_WELL',
          componentId: config.reportId
        }]
      }
    }]
  });
  return Object.assign({}, baseView, {
    state: JSON.stringify(state)
  });
}

// TODO: Add similar handling for report@0 when needed